import React, {
  FunctionComponent,
  useContext,
  useState,
  useEffect,
} from "react";
import styles from "./SubmitSolution.module.scss";
import { Challenge } from "shared/types/challenges";

import Grid from "@material-ui/core/Grid";
import MUButton from "@material-ui/core/Button";

import { useForm } from "react-hook-form";
import Button from "shared/components/Buttons/Button";
import { useParams, useHistory } from "react-router-dom";
import AppContext from "context/AppContext";
import apiCalls from "shared/utils/apiCalls";
import wrapLocalLoader from "shared/utils/wrapLocalLoader";
import useLogin from "shared/utils/useLogin";
import { KeyboardArrowLeftRounded } from "@material-ui/icons";
import ReactMarkdown from "react-markdown";
import CodeBlock from "shared/components/CodeBlock/CodeBlock";
import * as yup from "yup";
import NotFound from "shared/components/NotFound/NotFound";
import Skeleton from "@material-ui/lab/Skeleton";

import { Checkbox, withStyles } from "@material-ui/core";
import Modal from "shared/components/Modal/Modal";
import ErrorMessage from "shared/components/ErrorMessage/ErrorMessage";
import { Errors } from "shared/types/errors";
import firebase from "firebase/app";
import Input from "shared/components/Input/Input";
import { ReducerTypes } from "shared/types/reducerTypes";
import SubmittedMessage from "shared/components/SubmittedMessage/SubmittedMessage";
import { Solution } from "shared/types/solutions";

const checkBoxStyles = () => ({
  root: {
    paddingLeft: 0,
    "&$checked": {
      color: "var(--blue)",
    },

    "&": {
      color: "var(--blue)",
    },
  },
  checked: {},
});

const CustomCheckbox = withStyles(checkBoxStyles)(Checkbox);

enum FocusState {
  title = "title",
  description = "description",
  demoUrl = "demoUrl",
  repoUrl = "repoUrl",
  feedbackRequest = "feedbackRequest",
}

const helpTexts = {
  [FocusState.title]: {
    heading: "Title",
    description:
      "- You can use the challenge title or your own title. \n- The title will be shown in solution card",
  },
  [FocusState.description]: {
    heading: "Description",
    description:
      "- Use [Markdown](https://github.com/adam-p/markdown-here/wiki/Markdown-Here-Cheatsheet) to write and format your posts. \n- Try to explain the technologies you use and your experience while doing the challenge.",
  },
  [FocusState.demoUrl]: {
    heading: "Demo URL",
    description: "- Host your solution and paste the URL here",
  },
  [FocusState.repoUrl]: {
    heading: "Repository URL",
    description: "- Your Gihub repository",
  },
  [FocusState.feedbackRequest]: {
    heading: "Feedback Request",
    description:
      "- You want to know what other think about solution? Write down your request here",
  },
};

const SubmitSolution: FunctionComponent<{}> = () => {
  const { state, dispatch } = useContext(AppContext);
  const [challenge, setChallenge] = useState<Challenge>();
  const [focusState, setFocusState] = useState<FocusState>(FocusState.title);
  const [description, setDescription] = useState<string>("");
  const [preview, setPreview] = useState<boolean>();
  const [notFound, setNotFound] = useState<boolean>(false);
  const [solution, setSolution] = useState<any>(false);
  const [requestForFeedback, setRequestForFeedback] = useState<boolean>(true);
  const [showingSuccessModal, setShowingSuccessModal] = useState<boolean>(
    false
  );

  const [
    permissionDeclinedModalOpen,
    setPermissionDeclinedModalOpen,
  ] = useState(false);

  const [solutionLoading, setSolutionLoading] = useState(false);

  const validationSchema = yup.object({
    title: yup.string().min(1).max(100).required(),
    description: yup.string().min(1).max(10000).required(),
    demoUrl: yup.string().url().max(500).required(),
    repoUrl: yup.string().url().max(500).required(),
    feedbackRequest: yup.string().max(160),
  });

  const { register, handleSubmit, errors, setValue } = useForm({
    validationSchema,
    defaultValues: {
      title: "",
      description: "",
      demoUrl: "",
      repoUrl: "",
      feedbackRequest: "I would love to hear your feedback",
    },
  });

  const { id, solutionId } = useParams();
  const history = useHistory();

  const { getUserSolutions } = useLogin({
    dispatch,
  });

  const onSubmit = async (data: any) => {
    if (!state.user) {
      return;
    }

    if (state.user.reputations < 0) {
      setPermissionDeclinedModalOpen(true);
      return;
    }

    const submit = wrapLocalLoader(async () => {
      const res = await apiCalls(
        {
          method: "put",
          data: {
            ...data,
            ...(!requestForFeedback ? { feedbackRequest: "" } : []),
          },
          url: `/solutions/${solutionId}`,
        },
        dispatch
      );

      if (res) {
        dispatch({
          type: ReducerTypes.setSolutions,
          payload: { value: [res.data, ...state.solutions] },
        });

        if (res.data.id) {
          setShowingSuccessModal(true);
        }

        firebase.analytics().logEvent("solutions_submitted");

        await getUserSolutions(state.user?.uid);
      }
    }, setSolutionLoading);

    submit();
  };

  const currentPath = () => {
    if (challenge?.tags.includes("Responsive")) {
      return "Responsive";
    } else if (challenge?.tags.includes("Front-end")) {
      return "Front-end";
    } else {
      return "Full-stack";
    }
  };

  const getCurrentPathChallenges = () => {
    if (challenge?.tags.includes("Responsive")) {
      return state.challenges.filter((challenge: any) =>
        challenge.tags.includes("Responsive")
      );
    } else if (challenge?.tags.includes("Front-end")) {
      return state.challenges.filter((challenge: any) =>
        challenge.tags.includes("Front-end")
      );
    } else {
      return state.challenges.filter((challenge: any) =>
        challenge.tags.includes("Full-stack")
      );
    }
  };

  const getFinishedSolutions = () => {
    return state.userSolutions?.filter(
      (solution: Solution) =>
        getCurrentPathChallenges().filter(
          ({ id }: Challenge) => solution.challengeId === id
        ).length > 0
    );
  };

  const getChallenge = async () => {
    const res = await apiCalls(
      {
        method: "get",
        url: `/challenges/${id}`,
      },
      dispatch
    );

    if (res) {
      setChallenge(res.data);
    } else {
      setNotFound(true);
    }
  };

  const getSolution = async () => {
    setSolutionLoading(true);
    const res = await apiCalls(
      {
        method: "get",
        url: `/solutions/${solutionId}`,
      },
      dispatch
    );

    if (res) {
      setSolution(res.data);

      if (res.data.description) {
        setValue("description", res.data.description);
      }

      if (res.data.title) {
        setValue("title", res.data.title);
      }

      if (res.data.demoUrl) {
        setValue("demoUrl", res.data.demoUrl);
      }

      if (res.data.repoUrl) {
        setValue("repoUrl", res.data.repoUrl);
      }

      if (res.data.feedbackRequest) {
        setValue("feedbackRequest", res.data.feedbackRequest);
      }
    } else {
      setNotFound(true);
    }

    setSolutionLoading(false);
  };

  useEffect(() => {
    if (!challenge) {
      getChallenge();
    }

    getSolution();

    // eslint-disable-next-line
  }, []);

  if (!challenge || solutionLoading) {
    return (
      <div>
        <Skeleton height={225} variant="rect" />

        <Skeleton height={76} />

        <Grid container direction="row" spacing={4}>
          <Grid item xs={12} md={8}>
            <Skeleton height={80} variant="rect" />
            <Skeleton height={140} />
            <Skeleton height={80} variant="rect" />
            <Skeleton height={80} />
            <Skeleton height={80} variant="rect" />
          </Grid>
        </Grid>
      </div>
    );
  }

  if (solution.uid !== state.user?.uid) {
    return <div />;
  }

  if (notFound) {
    return <NotFound />;
  }

  return (
    <>
      <Modal
        open={permissionDeclinedModalOpen}
        closeModal={() => setPermissionDeclinedModalOpen(false)}
        size="sm"
      >
        <ErrorMessage errorType={Errors.reputationsDeclined} />
      </Modal>

      <Modal
        open={showingSuccessModal}
        closeModal={() => setShowingSuccessModal(!showingSuccessModal)}
        size="sm"
      >
        <SubmittedMessage
          numberOfProjects={getFinishedSolutions()?.length}
          path={currentPath()}
        />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginTop: "var(--space-2xl)",
          }}
        >
          <Button onClick={() => history.push(`/solutions/${solution.id}`)}>
            Go to Solution
          </Button>
        </div>
      </Modal>

      <div className={styles.submitSolution_challenge}>
        <Grid container direction="row" spacing={4}>
          <Grid item xs={12} md={4}>
            <div className={styles.submitSolution_challenge_imgWrapper}>
              <img
                loading="lazy"
                className={styles.submitSolution_challenge_thumbnailImage}
                src={challenge.thumbnailImage}
                alt={challenge.name}
              />
            </div>
          </Grid>

          <Grid item xs={12} md={6}>
            <MUButton
              variant="text"
              type="button"
              style={{
                marginBottom: "var(--space-md)",
                textTransform: "lowercase",
                color: "var(--primary-color-light)",
                fontFamily: "var(--heading-font-family)",
              }}
              size="small"
              startIcon={
                <KeyboardArrowLeftRounded
                  fontSize="inherit"
                  style={{
                    fontSize: "var(--text-xs)",
                  }}
                />
              }
              onClick={() => history.push(`/challenges/${id}`)}
            >
              Challenge
            </MUButton>

            <h4>{challenge.name}</h4>

            <p>- Make sure you fulfill all the user stories</p>
            <p>- Includes both demo URL and repository URL</p>
            <p>- Try to explain your approach briefly</p>
          </Grid>
        </Grid>
      </div>

      <h2>Submit Solution</h2>

      <Grid container direction="row" spacing={4}>
        <Grid item xs={12} md={8}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Input
              style={{ color: "red", borderColor: "red" }}
              InputLabelProps={{ shrink: true }}
              onFocus={() => setFocusState(FocusState.title)}
              name="title"
              inputRef={register}
              label="Title"
              variant="outlined"
              fullWidth
              margin="normal"
              autoFocus
              error={!!errors["title"]?.message}
              helperText={errors["title"] ? errors["title"].message : undefined}
            />

            <div
              style={{
                marginTop: "var(--space-sm)",
                display: !preview ? "none" : "block",
              }}
            >
              <ReactMarkdown
                renderers={{ code: CodeBlock }}
                source={description}
              ></ReactMarkdown>
            </div>

            <Input
              InputLabelProps={{ shrink: true }}
              onFocus={() => setFocusState(FocusState.description)}
              name="description"
              inputRef={register}
              label="Description"
              variant="outlined"
              fullWidth
              margin="none"
              rows="12"
              multiline
              style={{
                marginTop: "var(--space-sm)",
                display: preview ? "none" : "block",
              }}
              onChange={(e: any) => setDescription(e.target.value)}
              error={!!errors["description"]?.message}
              helperText={
                errors["description"]
                  ? errors["description"].message
                  : undefined
              }
            />

            <div className={styles.submitSolution_buttonsRow}>
              {preview ? (
                <MUButton
                  variant="text"
                  type="button"
                  style={{
                    textTransform: "lowercase",
                    fontFamily: "var(--heading-font-family)",
                    color: "var(--primary-color-xlight)",
                  }}
                  size="small"
                  onClick={() => setPreview(!true)}
                >
                  edit
                </MUButton>
              ) : (
                <MUButton
                  variant="text"
                  type="button"
                  style={{
                    textTransform: "lowercase",
                    fontFamily: "var(--heading-font-family)",
                    color: "var(--primary-color-xlight)",
                  }}
                  size="small"
                  onClick={() => setPreview(true)}
                >
                  Preview
                </MUButton>
              )}
            </div>

            <Input
              InputLabelProps={{ shrink: true }}
              onFocus={() => setFocusState(FocusState.demoUrl)}
              name="demoUrl"
              inputRef={register}
              label="Demo URL"
              variant="outlined"
              fullWidth
              margin="normal"
              error={!!errors["demoUrl"]?.message}
              helperText={
                errors["demoUrl"] ? errors["demoUrl"].message : undefined
              }
            />

            <Input
              InputLabelProps={{ shrink: true }}
              onFocus={() => setFocusState(FocusState.repoUrl)}
              name="repoUrl"
              inputRef={register}
              label="Repository URL"
              variant="outlined"
              fullWidth
              margin="normal"
              error={!!errors["repoUrl"]?.message}
              helperText={
                errors["repoUrl"] ? errors["repoUrl"].message : undefined
              }
            />

            <button
              className={styles.submitSolution_buttonReset}
              type="button"
              onClick={() => setRequestForFeedback(!requestForFeedback)}
            >
              <CustomCheckbox
                disabled
                color="default"
                checked={requestForFeedback}
              />
              <label>Request for feedback</label>
            </button>

            {requestForFeedback && (
              <Input
                InputLabelProps={{ shrink: true }}
                onFocus={() => setFocusState(FocusState.feedbackRequest)}
                name="feedbackRequest"
                inputRef={register}
                label="Feedback Request"
                variant="outlined"
                fullWidth
                margin="normal"
                error={!!errors["feedbackRequest"]?.message}
                helperText={
                  errors["feedbackRequest"]
                    ? errors["feedbackRequest"].message
                    : undefined
                }
                rows="2"
                multiline
              />
            )}

            <div className={styles.submitSolution_buttonsRow}>
              <Button
                type="submit"
                loading={solutionLoading}
                aria-label="submit"
              >
                Submit
              </Button>
            </div>
          </form>
        </Grid>

        <Grid item xs={12} md={4}>
          <div className={styles.submitSolution_inforCard}>
            <h4>{helpTexts[focusState].heading}</h4>

            <ReactMarkdown
              renderers={{ code: CodeBlock }}
              source={helpTexts[focusState].description}
            ></ReactMarkdown>
          </div>
        </Grid>
      </Grid>
    </>
  );
};

export default SubmitSolution;
